// @flow

import type {
  ActionHandlers,
  Reducer,
  Selector,
} from '@travel-frontend/state-handling-sdk';
import type {GetState} from 'redux/v1/insurance/get/GetState';
import type {Offer} from 'redux/v1/insurance/models';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {createSelector} from 'reselect';

import {DELETE_INSURANCES} from 'redux/v1/insurance/delete/actions';
import {FETCH_INSURANCES} from 'redux/v1/insurance/get/actions';
import {getState} from 'redux/v1/insurance/InsuranceState';

const mergeProducts = (incomingProducts: Offer[]): Offer[] => {
  if (!incomingProducts || incomingProducts.length === 0) {
    return [];
  }

  if (incomingProducts.length === 1) {
    return incomingProducts;
  }

  const products = incomingProducts.map((p) => ({...p}));

  const mainProductIndex = products.length - 1;
  const mainProduct = {...products[mainProductIndex]};

  const otherProducts = products.slice(0, -1);

  for (const other of otherProducts) {
    mainProduct.price += other.price;

    mainProduct.title = `${mainProduct.title} inkl. ${other.title
      .split('-')
      .pop()}`;
  }

  return [mainProduct];
};

export const initialState: GetState = {
  loading: false,
};

const actionCreators: ActionHandlers<GetState> = {
  [PENDING(FETCH_INSURANCES)]: (state: GetState) => ({
    ...state,
    loading: true,
  }),
  [SUCCESS(FETCH_INSURANCES)]: (state: GetState, action: any) => {
    const mergedProduct = mergeProducts(action.data.main);

    return {
      ...state,
      loading: false,
      insurances: {...action.data, main: mergedProduct},
    };
  },
  [ERROR(FETCH_INSURANCES)]: (state: GetState, action: any) => ({
    ...state,
    loading: false,
    insurances: undefined,
    error: action.error,
  }),
  [SUCCESS(DELETE_INSURANCES)]: (state: GetState, action: any) => ({
    ...state,
    loading: false,
    insurances: undefined,
  }),
};

export const getReducer: Reducer<GetState> =
  createReducerUsingActionHandlers<GetState>(initialState, actionCreators);

export const isInsuranceLoaded: Selector<boolean> = createSelector(
  getState,
  (state: GetState) => !state.loading && 'insurances' in state
);

export const getInsurances: Selector<{main: Offer[], additional: Offer[]}> =
  createSelector(
    [getState, isInsuranceLoaded],
    (state: GetState, loaded: boolean) => {
      return state?.insurances || undefined;
    }
  );
